/**
 * DestinationRow — one 44px row for a catalog destination inside a phone
 * sheet: icon, label, unread count where one exists, `aria-current` on the
 * one you are on. Shared by the bottom bar's More sheet and the mail view
 * picker; the caller resolves the count, so this stays free of app stores.
 */
import React from 'react';

import { DomainIcon } from './DomainIcon';
import { Icons } from './Icons';

import type { Destination } from '@shared/navigation/destinations';

interface DestinationRowProps {
  d: Destination;
  active: boolean;
  count: number | null;
  onOpen: (id: string) => void;
}

export const DestinationRow: React.FC<DestinationRowProps> = ({ d, active, count, onOpen }) => {
  const Glyph = Icons[d.icon];
  const showCount = count != null && count > 0;
  const name = showCount ? `${d.label} (${count})` : d.label;
  return (
    <li>
      <button
        type="button"
        aria-label={name}
        {...(active && { 'aria-current': 'page' as const })}
        onClick={() => onOpen(d.id)}
        className={`hover:bg-muted active:bg-muted flex min-h-11 w-full items-center gap-3 px-4 text-left text-sm ${
          active ? 'text-primary font-semibold' : 'text-foreground'
        }`}
      >
        {d.domain ? (
          <DomainIcon domain={d.domain} size="sm" />
        ) : (
          <Glyph size="sm" color="inherit" />
        )}
        <span className="min-w-0 flex-1 truncate">{d.label}</span>
        {showCount && (
          <span className="text-muted-foreground text-xs tabular-nums">{count}</span>
        )}
      </button>
    </li>
  );
};
